import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// Campaign Creation Types (updated to match prototype)
export type CampaignBasics = {
  brand: string
  logoUrl: string | null
  name: string
  dateType: 'exact' | 'timeframe' | null
  startDate: string | null
  endDate: string | null
  startTime: string
  endTime: string
  timeframeDays: number | null
  timezone: string
  socialPlatform: string[]
  budget: number | null
  licensing: boolean
  // The kind of deal, and whether athletes may apply to it (an opportunity)
  dealType: 'content' | 'gifting' | 'appearance' | 'licensing'
  openToApplications: boolean
  applicationDeadline: string | null
  payout: number | null
  /** Gifting: the bonus ladder for this campaign (null = the brand's default) */
  giftingBonuses: { views: number; amount: number }[] | null
  /** One of the brand's products (Settings > Brands), when the campaign promotes it */
  productId?: string | null
}

export type TargetingOptions = {
  sports: string[]
  geography: {
    states: string[]
    regions: string[]
    schools: string[]
  }
  gender: string[]
  ageRange: {
    min: number
    max: number
  }
  interests: string[]
  channelPerformance: {
    minViews: number
    minEngagementRate: number
  }
  creatorTier: string[]
  availability: string[]
  contentStyle: string[]
  // New fields for simplified targeting
  businessDescription?: string
  productDescription?: string
}

export type CampaignBriefData = {
  description: string
  objectives: string[]
  primaryKpi: string
  targetAgeRange: string
  targetGender: string
  targetLocation: string
  targetAudienceDescription: string
  primaryMessage: string
  supportingPoints: string[]
  cta: string
  tones: string[]
  creativeDirection: string
  additionalNotes: string
  /** What the brand is sending or promoting */
  productName?: string
  productDescription?: string
  productPhotoUrl?: string | null
  /** How many posts each athlete makes */
  postsPerAthlete?: number
}

export type CreativeOptions = {
  script: string
  logo: string | null
  bannerText: string
  // Creative brief fields
  briefText: string
  analyzedBrief: AnalyzedBrief | null
  campaignBrief: CampaignBriefData | null
}

export type AnalyzedBrief = {
  tags: string[]
  targetAudience: string
  keyThemes: string[]
  tone: string
  objectives: string[]
}

export type SelectedAthlete = {
  id: string
  name: string
  school: string
  sport: string
  followers: number
  imageUrl: string
  rate?: number
}

export type InvitedAthlete = {
  id: string
  name: string
  school: string
  sport: string
  followers: number
  imageUrl: string
  status: 'pending' | 'accepted' | 'declined'
  message?: string
}

export type CampaignCreationData = {
  basics: CampaignBasics
  targeting: TargetingOptions
  creative: CreativeOptions
  selectedAthletes: SelectedAthlete[]
  invitedAthletes: InvitedAthlete[]
}

interface CampaignCreationState {
  campaignData: CampaignCreationData
}

// Default values
const defaultCampaignData: CampaignCreationData = {
  basics: {
    brand: '',
    logoUrl: null,
    name: '',
    dateType: null,
    startDate: null,
    endDate: null,
    startTime: '12:00PM',
    endTime: '12:00PM',
    timeframeDays: null,
    timezone: 'Eastern Time',
    socialPlatform: ['TikTok'],
    budget: null,
    licensing: false,
    dealType: 'gifting',
    // Campaigns go to the Marketplace so athletes can apply; brands can turn this off
    openToApplications: true,
    applicationDeadline: null,
    payout: null,
    giftingBonuses: null,
  },
  targeting: {
    sports: [],
    geography: {
      states: [],
      regions: [],
      schools: [],
    },
    gender: [],
    ageRange: {
      min: 18,
      max: 24,
    },
    interests: [],
    channelPerformance: {
      minViews: 1000,
      minEngagementRate: 2,
    },
    creatorTier: [],
    availability: [],
    contentStyle: [],
    businessDescription: '',
    productDescription: '',
  },
  creative: {
    script: '',
    logo: null,
    bannerText: '',
    briefText: '',
    analyzedBrief: null,
    campaignBrief: null,
  },
  selectedAthletes: [],
  invitedAthletes: [],
}

const initialState: CampaignCreationState = {
  campaignData: defaultCampaignData,
}

const campaignCreationSlice = createSlice({
  name: 'campaignCreation',
  initialState,
  reducers: {
    updateBasics: (state, action: PayloadAction<Partial<CampaignBasics>>) => {
      state.campaignData.basics = {
        ...state.campaignData.basics,
        ...action.payload,
      }
    },
    updateTargeting: (state, action: PayloadAction<Partial<TargetingOptions>>) => {
      state.campaignData.targeting = {
        ...state.campaignData.targeting,
        ...action.payload,
      }
    },
    updateCreative: (state, action: PayloadAction<Partial<CreativeOptions>>) => {
      state.campaignData.creative = {
        ...state.campaignData.creative,
        ...action.payload,
      }
    },
    addSelectedAthlete: (state, action: PayloadAction<SelectedAthlete>) => {
      const exists = state.campaignData.selectedAthletes.find((a) => a.id === action.payload.id)
      if (!exists) {
        state.campaignData.selectedAthletes.push(action.payload)
      }
    },
    removeSelectedAthlete: (state, action: PayloadAction<string>) => {
      state.campaignData.selectedAthletes = state.campaignData.selectedAthletes.filter(
        (a) => a.id !== action.payload,
      )
    },
    updateSelectedAthleteRate: (state, action: PayloadAction<{ id: string; rate: number }>) => {
      const athlete = state.campaignData.selectedAthletes.find((a) => a.id === action.payload.id)
      if (athlete) {
        athlete.rate = action.payload.rate
      }
    },
    addInvitedAthlete: (state, action: PayloadAction<InvitedAthlete>) => {
      const exists = state.campaignData.invitedAthletes.find((a) => a.id === action.payload.id)
      if (!exists) {
        state.campaignData.invitedAthletes.push(action.payload)
      }
    },
    removeInvitedAthlete: (state, action: PayloadAction<string>) => {
      state.campaignData.invitedAthletes = state.campaignData.invitedAthletes.filter(
        (a) => a.id !== action.payload,
      )
    },
    updateInvitedAthleteStatus: (
      state,
      action: PayloadAction<{ id: string; status: 'pending' | 'accepted' | 'declined' }>,
    ) => {
      const athlete = state.campaignData.invitedAthletes.find((a) => a.id === action.payload.id)
      if (athlete) {
        athlete.status = action.payload.status
      }
    },
    resetCampaign: (state) => {
      state.campaignData = defaultCampaignData
    },
  },
})

export const {
  updateBasics,
  updateTargeting,
  updateCreative,
  addSelectedAthlete,
  removeSelectedAthlete,
  updateSelectedAthleteRate,
  addInvitedAthlete,
  removeInvitedAthlete,
  updateInvitedAthleteStatus,
  resetCampaign,
} = campaignCreationSlice.actions

export default campaignCreationSlice.reducer
